import { UserActionTypes, useUser } from '@myosh/myosh-login';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { api } from '../../../redux/services/api';
import { selectIsManualSchemaUpdate } from '../../../redux/slices/schema';
import { showError } from '../../../services/notification.service';
import { terminateDashboardSession } from '../../../utils/auth-util';
import ConfirmationDialog from '../../common/confirmation-dialog.component';

interface SchemaConfirmationModalProps {
  visible: boolean;
  schema?: string;
}

export function SchemaConfirmationModal({ visible = false, schema }: SchemaConfirmationModalProps) {
  const dispatch = useAppDispatch();
  const { state: userState, dispatch: userDispatch } = useUser();
  const isManualSchemaUpdate = useAppSelector(selectIsManualSchemaUpdate);
  const { t } = useTranslation();

  const [hasUserAccessToSchema, setHasUserAccessToSchema] = useState(false);
  const isSchemaConfirmationModalVisible = visible && !isManualSchemaUpdate && hasUserAccessToSchema;

  useEffect(() => {
    const schemas = userState.user?.schemas;
    if (schemas && schema) {
      const userSchemas = schemas.split(',');
      const hasAccessToSchema = userSchemas.includes(schema);
      if (!hasAccessToSchema) {
        showError(t('error', { message: t('permission-access-schema', { schema: schema }) }));
        onCancel();
      }

      setHasUserAccessToSchema(hasAccessToSchema);
    }
  }, [userState.user, schema, t]);

  const onConfirm = async () => {
    if (schema) {
      await terminateDashboardSession(userState.user?.currentSchema);
      userDispatch({ type: UserActionTypes.UpdateCurrentSchema, payload: schema });
      dispatch(api.util.resetApiState());
      window.location.replace(window.location.pathname);
    }
  };

  const onCancel = () => {
    window.location.replace('/');
  };

  return (
    <>
      <ConfirmationDialog
        header={t('warning')}
        visible={isSchemaConfirmationModalVisible}
        onConfirm={onConfirm}
        confirmButtonText={t('ok')}
        onClose={onCancel}
        hasCancelButton
      >
        {t('navigating-to-schema', { schema: schema })}
      </ConfirmationDialog>
    </>
  );
}
